import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";

const footerLinks = [
  { label: "Safety", path: "/safety" },
  { label: "Locations", path: "/locations" },
  { label: "Categories", path: "/categories" },
  { label: "Blog", path: "/blog" },
  { label: "Incall", path: "/services/incall" },
  { label: "Outcall", path: "/services/outcall" },
  { label: "About", path: "/about" },
  { label: "Contact", path: "/contact" },
  { label: "FAQ", path: "/faq" },
  { label: "Reviews", path: "/reviews" },
  { label: "Terms", path: "/terms" },
  { label: "Privacy", path: "/privacy-policy" },
];

interface SocialAccount {
  id: string;
  platform: string;
  username: string | null;
  profileUrl: string | null;
  isActive: boolean | null;
}

const SOCIAL_ICONS: Record<string, { icon: string; color: string; label: string }> = {
  twitter: { icon: "𝕏", color: "#1DA1F2", label: "Twitter" },
  telegram: { icon: "✈", color: "#0088cc", label: "Telegram" },
  instagram: { icon: "📷", color: "#E4405F", label: "Instagram" },
  facebook: { icon: "f", color: "#1877F2", label: "Facebook" },
  linkedin: { icon: "in", color: "#0A66C2", label: "LinkedIn" },
  tiktok: { icon: "♪", color: "#FFFFFF", label: "TikTok" },
};

function SocialIcon({ platform, url }: { platform: string; url: string }) {
  const cfg = SOCIAL_ICONS[platform];
  if (!cfg) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={cfg.label}
      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold border border-gray-700 hover:border-[#D4AF37] hover:scale-110 transition-all"
      style={{ backgroundColor: cfg.color + "20" }}
    >
      {cfg.icon}
    </a>
  );
}

export function Footer() {
  const { data: socialAccounts = [] } = useQuery<SocialAccount[]>({
    queryKey: ["/api/social/accounts"],
    staleTime: 5 * 60 * 1000,
  });

  const activeAccounts = socialAccounts.filter(a => a.isActive);

  return (
    <footer
      data-testid="footer"
      className="bg-[#0F0F0F] py-20 px-6 border-t border-gray-900 text-center"
    >
      <div className="max-w-7xl mx-auto">
        <div className="w-14 h-8 border-2 border-[#D4AF37] flex items-center justify-center mx-auto mb-8 rounded-sm">
          <span className="text-[#D4AF37] font-bold text-xl uppercase px-2">
            LEM
          </span>
        </div>
        <h4 className="text-white uppercase tracking-[0.4em] text-sm mb-8">
          Luxury Escorts Manchester
        </h4>
        <div className="flex flex-wrap justify-center gap-8 mb-12">
          {footerLinks.map((link) => (
            <Link
              key={link.label}
              href={link.path}
              data-testid={`link-footer-${link.label.toLowerCase()}`}
              className="text-[10px] text-gray-400 hover:text-[#D4AF37] uppercase tracking-widest transition-colors"
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Follow Us — Social Icons */}
        {activeAccounts.length > 0 && (
          <div className="mb-10">
            <p className="text-[10px] text-gray-500 uppercase tracking-[0.3em] mb-4">Follow Us</p>
            <div className="flex items-center justify-center gap-3">
              {activeAccounts.map((account) => (
                <SocialIcon
                  key={account.id}
                  platform={account.platform}
                  url={account.profileUrl || "#"}
                />
              ))}
            </div>
          </div>
        )}

        <p className="text-[10px] text-gray-400 max-w-2xl mx-auto leading-relaxed uppercase tracking-widest mb-6">
          &copy; {new Date().getFullYear()} Luxury Escorts Manchester. All profiles are of
          independent individuals aged 18+. Total discretion guaranteed.
          Manchester's premier escort agency.
        </p>
        <div className="text-[10px] text-gray-400 mb-6">
          <span className="uppercase tracking-widest">Manchester, Greater Manchester, UK</span>
          <span className="mx-2">|</span>
          <a href="tel:+447000000000" className="text-[#D4AF37] hover:underline">+44 7000 000 000</a>
          <span className="mx-2">|</span>
          <span>Available 24/7</span>
        </div>
        <div className="text-[9px] text-gray-400 max-w-3xl mx-auto leading-relaxed">
          <p>
            Luxury Escorts Manchester provides premium escort companionship services across Greater Manchester
            including Manchester City Centre, Salford, Stockport, Bolton, Altrincham, Didsbury, Trafford,
            Oldham, Bury, Wigan, Rochdale, and Cheshire. Available 24/7 for incall and outcall bookings.
          </p>
        </div>
      </div>
    </footer>
  );
}
